import { useEffect, useState } from "react";
import { Brain, Loader2, MessageSquareText, Plus, Save, Sparkles, Target, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#5A504B]">{label}</label>
    {hint && <p className="mb-2 text-[11px] font-light leading-relaxed text-[#91837C]">{hint}</p>}
    {children}
  </div>
);

// Docstate's dedicated AI sales brain — train Sara like a human closer.
// Fully isolated from the clinic brain.
export const ReBrainTab = () => {
  const [brain, setBrain] = useState(null);
  const [trainedAt, setTrainedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [brief, setBrief] = useState("");
  const [generating, setGenerating] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/realestate/brain");
      setBrain(data.brain);
      setTrainedAt(data.trained ? data.trained_at : null);
    } catch {
      /* quiet */
    }
  };
  useEffect(() => { load(); }, []);

  const setP = (k, v) => setBrain((b) => ({ ...b, persona: { ...b.persona, [k]: v } }));
  const setB = (k, v) => setBrain((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/realestate/brain", { brain });
      setBrain(data.brain);
      setTrainedAt(data.trained_at);
      toast.success("Sara's brain saved — she'll use it on every chat");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (!brief.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/realestate/brain/generate", { brief: brief.trim() }, { timeout: 120000 });
      setBrain(data.brain);
      toast.success("Draft brain generated — review, tweak, then Save");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const test = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/realestate/brain/test", { message: testMsg.trim() }, { timeout: 90000 });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!brain) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const qs = brain.qualifying_questions || [];
  const objs = brain.objections || [];

  return (
    <div className="space-y-7" data-testid="re-brain-tab">
      {/* Header */}
      <div className="glass rounded-3xl p-7">
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#C9A24B] to-[#8a6d1f] shadow-[0_8px_24px_rgba(201,162,75,0.25)]">
            <Brain className="h-6 w-6 text-white" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Sara — Docstate Sales Brain</h2>
            <p className="mt-1 text-sm font-light leading-relaxed text-[#91837C]">
              Train your AI advisor like a human closer. This brain is dedicated to Docstate and never shares anything with the clinic.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {trainedAt && (
              <span className="hidden rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700 sm:inline">
                Trained {new Date(trainedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </span>
            )}
            <button onClick={save} disabled={saving} data-testid="re-brain-save" className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Brain
            </button>
          </div>
        </div>
      </div>

      {/* AI quick-start */}
      <div className="glass rounded-3xl p-7" data-testid="re-brain-generate">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
          <Wand2 className="h-3.5 w-3.5" /> Quick-start · let AI draft the brain
        </p>
        <p className="mt-2 text-sm font-light leading-relaxed text-[#91837C]">
          Describe your agency and ideal buyers in a sentence or two — AI writes a full starter brain you can refine.
        </p>
        <textarea
          rows={3}
          className="input-lux mt-4 resize-none text-sm"
          placeholder="e.g. We're a boutique Dubai brokerage focused on off-plan investments in Dubai Marina, Downtown and JVC for overseas investors chasing high ROI and the Golden Visa."
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          data-testid="re-brain-brief"
        />
        <button onClick={generate} disabled={generating || !brief.trim()} data-testid="re-brain-generate-btn" className="btn-outline-lux mt-3 inline-flex items-center gap-2 px-5 py-2.5 text-xs disabled:opacity-60">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Draft
        </button>
      </div>

      <div className="grid gap-7 lg:grid-cols-2">
        {/* Persona */}
        <div className="glass rounded-3xl p-7" data-testid="re-brain-persona">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Persona & Voice</h3>
          <div className="mt-5 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name"><input className="input-lux text-sm" value={brain.persona?.name || ""} onChange={(e) => setP("name", e.target.value)} data-testid="re-brain-persona-name" /></Field>
              <Field label="Role"><input className="input-lux text-sm" value={brain.persona?.role || ""} onChange={(e) => setP("role", e.target.value)} data-testid="re-brain-persona-role" /></Field>
            </div>
            <Field label="Languages"><input className="input-lux text-sm" value={brain.persona?.languages || ""} onChange={(e) => setP("languages", e.target.value)} data-testid="re-brain-persona-languages" /></Field>
            <Field label="Reply language" hint="Auto mirrors the customer (English → English, Hinglish → Hinglish, Arabic → Arabic). Or force one.">
              <select className="input-lux text-sm" value={brain.reply_language || "auto"} onChange={(e) => setB("reply_language", e.target.value)} data-testid="re-brain-reply-language">
                <option value="auto">Auto — mirror the customer</option>
                <option value="english">Always English</option>
                <option value="hinglish">Always Hinglish (Hindi in English letters)</option>
                <option value="arabic">Always Arabic</option>
              </select>
            </Field>
            <Field label="Tone" hint="How Sara should sound to clients."><textarea rows={2} className="input-lux resize-none text-sm" value={brain.persona?.tone || ""} onChange={(e) => setP("tone", e.target.value)} data-testid="re-brain-persona-tone" /></Field>
            <Field label="Opening line" hint="Her first message when a chat starts."><textarea rows={2} className="input-lux resize-none text-sm" value={brain.persona?.intro || ""} onChange={(e) => setP("intro", e.target.value)} data-testid="re-brain-persona-intro" /></Field>
          </div>
        </div>

        {/* Playbook + closing */}
        <div className="glass rounded-3xl p-7" data-testid="re-brain-playbook">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Sales Playbook & Closing</h3>
          <div className="mt-5 space-y-5">
            <Field label="Playbook" hint="The step-by-step approach Sara follows to move a lead forward."><textarea rows={6} className="input-lux resize-none text-sm" value={brain.playbook || ""} onChange={(e) => setB("playbook", e.target.value)} data-testid="re-brain-playbook-text" /></Field>
            <Field label="Closing & handover rules" hint="When to recommend, book a viewing or hand off to an agent."><textarea rows={5} className="input-lux resize-none text-sm" value={brain.closing_rules || ""} onChange={(e) => setB("closing_rules", e.target.value)} data-testid="re-brain-closing" /></Field>
          </div>
        </div>
      </div>

      {/* Qualifying questions */}
      <div className="glass rounded-3xl p-7" data-testid="re-brain-questions">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Qualifying Questions</h3>
          <button onClick={() => setB("qualifying_questions", [...qs, ""])} data-testid="re-brain-add-question" className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2 text-[11px]"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
        <p className="mt-1 text-[11px] font-light text-[#91837C]">Sara asks these conversationally, one at a time.</p>
        <div className="mt-5 space-y-3">
          {qs.map((q, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F8F3E6] text-[11px] font-semibold text-[#B38B2D]">{i + 1}</span>
              <input className="input-lux flex-1 text-sm" value={q} onChange={(e) => setB("qualifying_questions", qs.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`re-brain-question-${i}`} />
              <button onClick={() => setB("qualifying_questions", qs.filter((_, j) => j !== i))} aria-label="Remove" className="flex h-9 w-9 items-center justify-center rounded-full text-[#C9B8A8] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!qs.length && <p className="text-sm font-light text-[#91837C]">No questions yet — add a few to guide the conversation.</p>}
        </div>
      </div>

      {/* Objections */}
      <div className="glass rounded-3xl p-7" data-testid="re-brain-objections">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Objection Handling</h3>
          <button onClick={() => setB("objections", [...objs, { objection: "", response: "" }])} data-testid="re-brain-add-objection" className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2 text-[11px]"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
        <p className="mt-1 text-[11px] font-light text-[#91837C]">Teach Sara how to respond when buyers push back.</p>
        <div className="mt-5 space-y-4">
          {objs.map((o, i) => (
            <div key={i} className="rounded-2xl border border-[#EBE3DA] bg-white/60 p-4" data-testid={`re-brain-objection-${i}`}>
              <div className="flex items-center gap-3">
                <input className="input-lux flex-1 text-sm" placeholder="Objection (what the buyer says)" value={o.objection} onChange={(e) => setB("objections", objs.map((x, j) => (j === i ? { ...x, objection: e.target.value } : x)))} data-testid={`re-brain-objection-q-${i}`} />
                <button onClick={() => setB("objections", objs.filter((_, j) => j !== i))} aria-label="Remove" className="flex h-9 w-9 items-center justify-center rounded-full text-[#C9B8A8] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <textarea rows={2} className="input-lux mt-3 resize-none text-sm" placeholder="How Sara should respond" value={o.response} onChange={(e) => setB("objections", objs.map((x, j) => (j === i ? { ...x, response: e.target.value } : x)))} data-testid={`re-brain-objection-a-${i}`} />
            </div>
          ))}
          {!objs.length && <p className="text-sm font-light text-[#91837C]">No objections yet.</p>}
        </div>
      </div>

      {/* Knowledge base */}
      <div className="glass rounded-3xl p-7" data-testid="re-brain-knowledge">
        <h3 className="font-serif text-lg font-semibold text-[#1A1615]">Knowledge Base</h3>
        <p className="mt-1 text-[11px] font-light leading-relaxed text-[#91837C]">
          Facts Sara can use to answer and build trust — communities, developers, payment plans, ROI, Golden Visa, fees & process. (She never invents specific listings or prices — those come from your live inventory.)
        </p>
        <textarea rows={8} className="input-lux mt-4 resize-none text-sm" value={brain.knowledge_base || ""} onChange={(e) => setB("knowledge_base", e.target.value)} data-testid="re-brain-knowledge-text" />
      </div>

      {/* Playground */}
      <div className="glass rounded-3xl p-7" data-testid="re-brain-playground">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]"><Target className="h-3.5 w-3.5" /> Test-drive Sara</p>
        <p className="mt-2 text-sm font-light text-[#91837C]">Write like a buyer. This uses your saved brain live, but never creates real leads or viewings.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input className="input-lux flex-1 text-sm" placeholder="e.g. Hi, looking for a 2-bed in Dubai Marina around 2M" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && test()} data-testid="re-brain-test-input" />
          <button onClick={test} disabled={testing || !testMsg.trim()} data-testid="re-brain-test-send" className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />} Ask Sara
          </button>
        </div>
        {result && (
          <div className="mt-5 space-y-3" data-testid="re-brain-test-result">
            <div className="rounded-2xl border border-[#C9A24B]/30 bg-[#F8F3E6] p-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f]">Sara replies</p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[#1A1615]">{result.reply}</p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="rounded-full bg-white px-3 py-1 font-semibold text-[#5A504B]">action: {result.action}</span>
              {Object.entries(result.lead || {}).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} className="rounded-full bg-white px-3 py-1 text-[#5A504B]"><b className="text-[#1A1615]">{k}</b>: {String(v)}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
